import { Component, computed, effect, ElementRef, EventEmitter, Input, input, Output, viewChild } from '@angular/core';
import { Breakpoint, InstaLearnTopic } from '../../../common/models';
import { SwiperComponent } from '../../../common/components';
import { ColorHelperService } from '../../../common/services';



@Component({
    selector: 'lib-insta-learn-topics-widget',
    templateUrl: './insta-learn-topics-widget.component.html',
    styleUrls: ['./insta-learn-topics-widget.component.scss'],
    standalone: false
})
export class InstaLearnTopicsWidgetComponent {
  public swiperComponent = viewChild(SwiperComponent);

  @Input() public title: string;
  @Input() public textColor: string;
  @Input() public set highlightColor(value: string) {
    this._highlightColor = value;
    this._highlightTextColor = value ? this.colorHelperSerivce.getContrastingTextColor(value) : null;

    this.applyHighlightColors();
  }

  @Input() public items: InstaLearnTopic[] = [];

  @Input() public animation: string;
  @Input() public animationDelay = 0;
  public previewContainerBreakpoint = input<Breakpoint>();

  @Output() public openTopic: EventEmitter<InstaLearnTopic> = new EventEmitter();

  private _highlightColor: string | null = null;
  private _highlightTextColor: string | null = null;

  public swiperOptions = computed(() => {
    const breakpoint = this.previewContainerBreakpoint() ?? Breakpoint.DESKTOP;

    const mobileOptions = {
      slidesPerView: 'auto',
      spaceBetween: 16,
      slidesOffsetBefore: 16,
      slidesOffsetAfter: 16
    };

    const tabletOptions = {
      slidesPerView: 'auto',
      spaceBetween: 20,
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 0
    };

    const options = {
      slidesPerView: 'auto',
      spaceBetween: 16,
      slidesPerGroup: 2,
      loop: false
    };

    const configsOverrides = {
      [Breakpoint.DESKTOP]: undefined,
      [Breakpoint.MOBILE]: mobileOptions,
      [Breakpoint.TABLET]: tabletOptions
    };

    const desktop = {
      0: mobileOptions,
      768: tabletOptions,
      1024: { ...tabletOptions, spaceBetween: 24 }
    };

    const override = configsOverrides[breakpoint];

    return {
      ...options,
      breakpoints: Object.fromEntries(
        Object.entries(desktop).map(([width, config]) => [
          width,
          override ?? config
        ])
      )
    };
  });

  constructor(
    private readonly element: ElementRef,
    private readonly colorHelperSerivce: ColorHelperService) {
    effect(() => {
      const breakpont = this.previewContainerBreakpoint();
      const swiperComponent = this.swiperComponent();

      if (!swiperComponent?.swiper || !breakpont) {
        return;
      }

      const bp: 0 | 768 | 1024 = swiperComponent.swiper.currentBreakpoint ?? 0;
      const options = this.swiperOptions();
      const bpParams = options.breakpoints[bp] ?? options.breakpoints[0];

      Object.assign(swiperComponent.swiperContainerRef.nativeElement, bpParams);
      Object.assign(swiperComponent.swiper.params, bpParams);
      swiperComponent.swiper.update();
    });
  }


  private applyHighlightColors(): void {
    const style = this.element.nativeElement.style;

    if (this._highlightColor) {
      style.setProperty('--lms-topic-widget-highlight-color', this._highlightColor);
    } else {
      style.removeProperty('--lms-topic-widget-highlight-color');
    }

    if (this._highlightTextColor) {
      style.setProperty('--lms-topic-widget-highlight-text-color', this._highlightTextColor);
    } else {
      style.removeProperty('--lms-topic-widget-highlight-text-color');
    }
  }
}
